import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { DollarSign, Wallet, Percent, CalendarClock, ArrowRight, Lock, Info } from 'lucide-react';
import { Slider } from '../ui/slider';
import { Switch } from '../ui/switch';
import { CalcField } from './CalcField';
import { payment, TAX_RATE } from './loanMath';
import { AnimatedMoney } from './AnimatedMoney';

const COMPARE = [48, 60, 72, 84];

export const PaymentCalculator = ({ c, en }) => {
  const [price, setPrice] = useState(25000);
  const [down, setDown] = useState(0);
  const [rate, setRate] = useState(12.99);
  const [months, setMonths] = useState(72);
  const [taxes, setTaxes] = useState(false);
  const [freq, setFreq] = useState('monthly');

  const financed = Math.max(0, (Number(price) || 0) * (taxes ? 1 + TAX_RATE : 1) - (Number(down) || 0));
  const r = Number(rate) || 0;
  const res = useMemo(() => payment(financed, r, months, freq), [financed, r, months, freq]);

  return (
    <div className="bg-white rounded-3xl border border-emerald-100 border-t-4 border-t-[#1a5c3b] shadow-[0_30px_70px_rgba(26,92,59,0.15)] overflow-hidden" data-testid="loan-calculator">
      <div className="p-6 md:p-8 space-y-5">
        <p className="text-xl font-bold text-[#0f172a]">{c.calcTitle}</p>
        <CalcField label={c.amount} icon={DollarSign} value={price} onChange={setPrice} suffix="$" step={500} max={500000} testid="calc-price" />
        <label className="flex items-center justify-between gap-3 text-sm text-slate-600 cursor-pointer">
          <span>{c.taxes}</span>
          <Switch className="data-[state=checked]:bg-[#1a5c3b]" checked={taxes} onCheckedChange={setTaxes} data-testid="calc-taxes" />
        </label>
        <div className="grid grid-cols-2 gap-4">
          <CalcField label={c.down} icon={Wallet} value={down} onChange={setDown} suffix="$" step={500} max={500000} testid="calc-down" />
          <CalcField label={c.rate} icon={Percent} value={rate} onChange={setRate} suffix="%" step={0.1} max={40} testid="calc-rate" />
        </div>
        <div>
          <div className="flex items-center justify-between text-sm font-semibold text-slate-700 mb-3">
            <span className="flex items-center gap-2"><CalendarClock className="w-4 h-4 text-[#1a5c3b]" />{c.term}</span>
            <span className="text-[#1a5c3b] bg-emerald-50 px-3 py-1 rounded-full" data-testid="calc-term-label">{months} {c.months} · {+(months / 12).toFixed(1)} {c.years}</span>
          </div>
          <Slider className="[&_.bg-primary]:bg-[#1a5c3b] [&_.bg-primary\/20]:bg-emerald-100 [&_[role=slider]]:h-6 [&_[role=slider]]:w-6 [&_[role=slider]]:border-2 [&_[role=slider]]:border-[#1a5c3b] [&>span:first-child]:h-2.5" value={[months]} min={12} max={96} step={12} onValueChange={([v]) => setMonths(v)} data-testid="calc-term" />
        </div>
        <div>
          <p className="text-sm font-semibold text-slate-700 mb-2">{c.freq}</p>
          <div className="grid grid-cols-3 gap-2">
            {Object.entries(c.freqs).map(([k, label]) => (
              <button key={k} type="button" onClick={() => setFreq(k)} className={`py-2.5 px-1 rounded-xl text-xs sm:text-sm font-semibold border-2 transition-colors ${freq === k ? 'border-[#1a5c3b] bg-[#1a5c3b] text-white' : 'border-gray-200 text-slate-600 hover:border-[#1a5c3b]/50'}`} data-testid={`calc-freq-${k}`}>
                {label}
              </button>
            ))}
          </div>
        </div>
      </div>

      <div className="bg-gradient-to-br from-[#1a5c3b] to-[#0f3d26] text-white p-6 md:p-8">
        <p className="text-emerald-200 text-sm font-semibold uppercase tracking-wider">{c.result}</p>
        <p className="text-4xl md:text-5xl font-extrabold tabular-nums mt-1" data-testid="calc-result">
          <AnimatedMoney value={res.per} en={en} testid="calc-result-value" /> <span className="text-lg font-semibold text-emerald-200">{c.per[freq]}</span>
        </p>
        <div className="grid grid-cols-3 gap-3 mt-5 text-sm">
          {[[c.financed, financed, 'calc-financed'], [c.interest, res.interest, 'calc-interest'], [c.total, res.total, 'calc-total']].map(([l, v, id]) => (
            <div key={id} className="bg-white/10 rounded-xl p-3">
              <p className="text-emerald-200 text-xs">{l}</p>
              <p className="font-bold" data-testid={id}><AnimatedMoney value={v} en={en} decimals={0} /></p>
            </div>
          ))}
        </div>
        <p className="text-sm font-semibold mt-6 mb-2">{c.compare}</p>
        <div className="grid grid-cols-4 gap-2" data-testid="calc-compare">
          {COMPARE.map((m) => (
            <button key={m} type="button" onClick={() => setMonths(m)} className={`rounded-xl p-2.5 text-center transition-colors ${months === m ? 'bg-white text-[#1a5c3b]' : 'bg-white/10 hover:bg-white/20'}`} data-testid={`calc-compare-${m}`}>
              <p className="text-xs opacity-80">{m} {c.months}</p>
              <p className="font-bold text-sm"><AnimatedMoney value={payment(financed, r, m, freq).per} en={en} decimals={0} /></p>
            </button>
          ))}
        </div>
        <div className="flex flex-col sm:flex-row gap-3 mt-6">
          <Link to="/verifier-admissibilite" className="flex-1 inline-flex items-center justify-center gap-2 bg-white text-[#1a5c3b] font-bold px-5 py-3.5 rounded-full hover:scale-[1.02] transition-transform" data-testid="calc-prequal-link">
            {c.prequal}<ArrowRight className="w-4 h-4" />
          </Link>
          <Link to="/demander-un-pret-auto" className="flex-1 inline-flex items-center justify-center border-2 border-white/60 text-white font-semibold px-5 py-3.5 rounded-full hover:bg-white/10 transition-colors" data-testid="calc-apply-link">
            {c.apply}
          </Link>
        </div>
      </div>
      <div className="grid sm:grid-cols-2 gap-4 p-6 md:px-8 bg-[#f8faf7] text-xs text-slate-500">
        {c.notes.map(([t, d], i) => (
          <div key={t} className="flex gap-2">
            {i === 0 ? <Lock className="w-4 h-4 text-[#1a5c3b] shrink-0" /> : <Info className="w-4 h-4 text-[#1a5c3b] shrink-0" />}
            <p><strong className="text-slate-700">{t} :</strong> {d}</p>
          </div>
        ))}
      </div>
    </div>
  );
};
